import { ref, computed, onMounted, onUnmounted, type Ref, type ComputedRef } from 'vue';
import { eventBus } from '@/libs/EventBus';
import { getNetWorkStatus, upgradedNetworkListener, basicNetworkListener } from '@/capacitors/utils';
import { useEmployeeStore } from '@/stores/employee';
import { logInfo } from '@/utils/logger';
import { NetworkQuality, ServerNetwork, ServerNetworkStatus } from '@/types/network.type';

// 24-hour cooldown for Poor network auto-popup
const NETWORK_ALERT_KEY = 'network_alert_next_allowed_at';
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

export interface UseNetworkStatusReturn {
  onLine: Ref<boolean>;
  networkSpeed: Ref<number>;
  networkLatency: Ref<number>;
  showNetworkAlert: Ref<boolean>;
  networkLevel: ComputedRef<NetworkQuality>;
  networkStatusKey: ComputedRef<string>;
  networkPopperClass: ComputedRef<string>;
  dismissNetworkAlert: () => void;
}

export function useNetworkStatus(): UseNetworkStatusReturn {
  const networkTimeout = ref<number | null>(null);
  const onLine = ref(true);
  const networkSpeed = ref(50);
  const networkLatency = ref(0);
  const showNetworkAlert = ref(false);
  const poorConfirmCount = ref(0); // require 2 consecutive Poor readings before alert

  const canAutoShowNetworkAlert = () => {
    try {
      const nextAt = Number(localStorage.getItem(NETWORK_ALERT_KEY));
      return !Number.isFinite(nextAt) || Date.now() >= nextAt;
    } catch { return true; }
  };

  const dismissNetworkAlert = () => {
    showNetworkAlert.value = false;
    try { localStorage.setItem(NETWORK_ALERT_KEY, String(Date.now() + COOLDOWN_MS)); } catch {}
  };

  // Network level: 1=poor, 2=moderate, 3=healthy
  const networkLevel = computed(() =>
    (networkSpeed.value < 15 || networkLatency.value >= 150) ? NetworkQuality.Poor :
    (networkSpeed.value < 50 || networkLatency.value >= 50) ? NetworkQuality.Moderate : NetworkQuality.Healthy
  );
  const networkStatusKey = computed(() =>
    !onLine.value ? 'offline' :
    networkLevel.value === NetworkQuality.Poor ? 'poor' :
    networkLevel.value === NetworkQuality.Moderate ? 'moderate' : 'healthy'
  );
  const networkPopperClass = computed(() => `network-popover network-popover--${networkStatusKey.value}`);

  // Named handler for proper cleanup
  const handleNetworkSpeed = ({ mbps, latency }: { mbps: number; latency: number }) => {
    const oldLevel = networkLevel.value;
    networkSpeed.value = mbps;
    networkLatency.value = latency;
    const newLevel = networkLevel.value;
    if (newLevel === NetworkQuality.Poor) {
      poorConfirmCount.value++;
      if (poorConfirmCount.value >= 2 && canAutoShowNetworkAlert()) showNetworkAlert.value = true;
    } else {
      poorConfirmCount.value = 0;
      if (oldLevel === NetworkQuality.Poor) showNetworkAlert.value = false; // immediate recovery
    }
  };

  const startNetworkTimeout = async (network: ServerNetwork) => {
    clearNetworkTimeout(network);
    logInfo('Start Network Timeout', network);
    networkTimeout.value = window.setTimeout(() => {
      const store = useEmployeeStore();
      store.logout();
    }, 300000);
  };

  const clearNetworkTimeout = (network: ServerNetwork) => {
    if (networkTimeout.value) {
      logInfo('Clear Network Timeout', network);
      window.clearTimeout(networkTimeout.value);
      networkTimeout.value = null;
    }
  };

  // Listener cleanups - register immediately in setup scope, not in onMounted
  const cleanUpBasicNetworkListener = basicNetworkListener((isOnline: boolean) => {
    onLine.value = isOnline;
    if (!isOnline) showNetworkAlert.value = true;
    eventBus.emit('connectedChange', isOnline);
  });

  let cleanUpUpgradedNetworkListener: (() => void) | undefined;
  const handleWsInit = () => {
    cleanUpBasicNetworkListener?.();
    cleanUpUpgradedNetworkListener?.();
    cleanUpUpgradedNetworkListener = upgradedNetworkListener((network: ServerNetwork) => {
      logInfo('Network Change', network);
      if (network.status == ServerNetworkStatus.reconnecting || network.status == ServerNetworkStatus.connected) clearNetworkTimeout(network);
      if (network.status == ServerNetworkStatus.closed) startNetworkTimeout(network);
      onLine.value = network.isOnline;
      if (!network.isOnline) showNetworkAlert.value = true;
      eventBus.emit('connectedChange', onLine.value);
    });
  };
  eventBus.on('websocketInitialized', handleWsInit);

  onMounted(async () => {
    eventBus.on('networkSpeed', handleNetworkSpeed);
    const result = await getNetWorkStatus();
    onLine.value = result.connected;
  });

  onUnmounted(() => {
    if (networkTimeout.value) { window.clearTimeout(networkTimeout.value); networkTimeout.value = null; }
    eventBus.off('networkSpeed', handleNetworkSpeed);
    eventBus.off('websocketInitialized', handleWsInit);
    cleanUpUpgradedNetworkListener?.();
    cleanUpBasicNetworkListener?.();
  });

  return {
    onLine,
    networkSpeed,
    networkLatency,
    showNetworkAlert,
    networkLevel,
    networkStatusKey,
    networkPopperClass,
    dismissNetworkAlert,
  };
}
